/**
 * SnapScreenEditor — framework-neutral screenshot preview + expanded annotation editor.
 *
 * Variants:
 * - overlay (default): SDK product chrome; compact preview + fullscreen expand.
 * - embedded: host dialog owns the frame (native Panel); editor is the focal workspace
 *   inside that frame — not a second-generation widget pasted into a legacy column.
 *
 * Reuses AnnotationSession as the single edit model. No second annotation SoR.
 */

import { AnnotationSession, type AnnotationTool } from '../annotation/annotationSession';
import { safeText } from '../security/sanitize';

export type SnapScreenEditorVariant = 'overlay' | 'embedded';

export interface SnapScreenEditorOptions {
  /** Compact preview host (e.g. report form canvas wrap or Panel shared host). */
  previewMount: HTMLElement;
  /** Where the fullscreen overlay is appended (default: document.body). */
  overlayMount?: HTMLElement;
  session: AnnotationSession;
  /**
   * overlay = SDK default. embedded = Panel modal (host owns chrome; canvas is hero).
   */
  variant?: SnapScreenEditorVariant;
  /** When true, expanded editor opens automatically after setSource. */
  autoExpand?: boolean;
  onChange?: () => void;
  onRetake?: () => void;
  onConfirm?: () => void;
  onExpandedChange?: (expanded: boolean) => void;
  /** Optional status live region for a11y feedback. */
  statusTarget?: HTMLElement | null;
}

type ToolDef = { tool: AnnotationTool; label: string };

const ANNOTATE_TOOLS: ToolDef[] = [
  { tool: 'select', label: 'Select' },
  { tool: 'crop', label: 'Crop' },
  { tool: 'cover', label: 'Redact' },
  { tool: 'rect', label: 'Rectangle' },
  { tool: 'arrow', label: 'Arrow' },
  { tool: 'text', label: 'Text' },
  { tool: 'freehand', label: 'Freehand' }
];

export class SnapScreenEditor {
  readonly previewCanvas: HTMLCanvasElement;
  private readonly root: HTMLElement;
  private readonly previewWrap: HTMLElement;
  private readonly compactTools: HTMLElement;
  private readonly expandBtn: HTMLButtonElement;
  private readonly retakeBtn: HTMLButtonElement;
  private readonly removeBtn: HTMLButtonElement;
  private readonly overlay: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly viewport: HTMLElement;
  private readonly editorTools: HTMLElement;
  private readonly overlayMount: HTMLElement;
  private readonly session: AnnotationSession;
  private readonly variant: SnapScreenEditorVariant;
  private unbindCanvas: (() => void) | null = null;
  private unbindSession: (() => void) | null = null;
  private unbindKeys: (() => void) | null = null;
  private unbindPan: (() => void) | null = null;
  private expanded = false;
  private zoom = 1;
  private panX = 0;
  private panY = 0;
  private compactToolButtons: HTMLButtonElement[] = [];
  private editorToolButtons: HTMLButtonElement[] = [];
  private readonly opts: SnapScreenEditorOptions;

  constructor(opts: SnapScreenEditorOptions) {
    this.opts = opts;
    this.session = opts.session;
    this.overlayMount = opts.overlayMount || document.body;
    this.variant = opts.variant === 'embedded' ? 'embedded' : 'overlay';

    this.root = document.createElement('div');
    this.root.className =
      this.variant === 'embedded'
        ? 'nv-snap-screen-workspace nv-snap-screen-embedded'
        : 'nv-snap-screen-workspace nv-snap-screen-overlay-variant';
    this.root.setAttribute('data-nv-screen-variant', this.variant);

    this.previewWrap = document.createElement('div');
    this.previewWrap.className = 'nv-snap-canvas-wrap nv-snap-screen-preview';
    this.previewWrap.hidden = true;

    this.previewCanvas = document.createElement('canvas');
    this.previewCanvas.className = 'nv-snap-screen-canvas';
    this.previewCanvas.setAttribute('aria-label', 'Screenshot preview');
    this.previewWrap.appendChild(this.previewCanvas);

    this.compactTools = this.buildToolBar(false);
    this.compactToolButtons = Array.from(
      this.compactTools.querySelectorAll<HTMLButtonElement>('[data-nv-tool]')
    );

    this.expandBtn = this.btn(
      this.variant === 'embedded' ? 'Expand for precision' : 'Expand editor',
      () => this.openExpanded(),
      this.variant === 'embedded'
    );
    this.expandBtn.setAttribute('aria-label', 'Open expanded screenshot editor');
    this.expandBtn.classList.add('nv-snap-btn-expand');
    this.expandBtn.hidden = true;

    this.retakeBtn = this.btn('Retake', () => this.opts.onRetake?.());
    this.retakeBtn.hidden = true;
    this.removeBtn = this.btn('Remove', () => this.clear());
    this.removeBtn.classList.add('nv-snap-btn-danger');
    this.removeBtn.setAttribute('aria-label', 'Remove screenshot');
    this.removeBtn.hidden = true;

    const evidenceBar = document.createElement('div');
    evidenceBar.className = 'nv-snap-evidence-bar';
    evidenceBar.setAttribute('role', 'group');
    evidenceBar.setAttribute('aria-label', 'Screenshot actions');
    evidenceBar.append(this.expandBtn, this.retakeBtn, this.removeBtn);

    // Canvas is the hero: chrome → preview → actions (not tools floating over empty space).
    this.root.append(this.compactTools, this.previewWrap, evidenceBar);
    opts.previewMount.appendChild(this.root);

    this.overlay = document.createElement('div');
    this.overlay.className = 'nv-snap-editor';
    this.overlay.hidden = true;
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute(
      'aria-label',
      this.variant === 'embedded' ? 'Precision Workspace' : 'Screenshot evidence editor'
    );
    this.overlay.dataset.variant = this.variant;

    const header = document.createElement('div');
    header.className = 'nv-snap-editor-header';
    const title = document.createElement('h2');
    title.className = 'nv-snap-editor-title';
    safeText(title, this.variant === 'embedded' ? 'Precision Workspace' : 'Edit evidence');

    const zoomBar = document.createElement('div');
    zoomBar.className = 'nv-snap-editor-toolbar nv-snap-editor-zoom';
    zoomBar.setAttribute('role', 'group');
    zoomBar.setAttribute('aria-label', 'Zoom');
    zoomBar.append(
      this.btn('Fit', () => this.setZoomFit()),
      this.btn('Actual size', () => this.setZoomActual()),
      this.btn('Zoom in', () => this.adjustZoom(0.2)),
      this.btn('Zoom out', () => this.adjustZoom(-0.2))
    );

    if (this.variant === 'embedded') {
      title.classList.add('nv-snap-editor-title-precision');
      const subtitle = document.createElement('p');
      subtitle.className = 'nv-snap-editor-subtitle';
      safeText(subtitle, 'Focused editing — return to the request when finished.');
      const titleBlock = document.createElement('div');
      titleBlock.className = 'nv-snap-editor-heading';
      titleBlock.append(title, subtitle);
      header.append(titleBlock, zoomBar);
    } else {
      header.append(title, zoomBar);
    }

    this.editorTools = this.buildToolBar(true);
    this.editorToolButtons = Array.from(
      this.editorTools.querySelectorAll<HTMLButtonElement>('[data-nv-tool]')
    );

    this.stage = document.createElement('div');
    this.stage.className = 'nv-snap-editor-stage';
    this.stage.setAttribute('aria-label', 'Screenshot editing surface');
    this.viewport = document.createElement('div');
    this.viewport.className = 'nv-snap-editor-viewport';
    this.stage.appendChild(this.viewport);

    const footer = document.createElement('div');
    footer.className = 'nv-snap-editor-footer';
    const doneLabel = this.variant === 'embedded' ? 'Done' : 'Confirm evidence';
    const backLabel = this.variant === 'embedded' ? 'Back to request' : 'Collapse editor';
    // Embedded: return first (safe), then retake, then Done (primary) — mirrors Panel footer rhythm.
    if (this.variant === 'embedded') {
      footer.append(
        this.btn(backLabel, () => this.closeExpanded()),
        this.btn('Retake', () => {
          this.closeExpanded();
          this.opts.onRetake?.();
        }),
        this.btn(doneLabel, () => {
          this.closeExpanded();
          this.opts.onConfirm?.();
        }, true)
      );
    } else {
      footer.append(
        this.btn(doneLabel, () => {
          this.closeExpanded();
          this.opts.onConfirm?.();
        }, true),
        this.btn('Retake', () => {
          this.closeExpanded();
          this.opts.onRetake?.();
        }),
        this.btn(backLabel, () => this.closeExpanded())
      );
    }

    this.overlay.append(header, this.editorTools, this.stage, footer);
    this.overlayMount.appendChild(this.overlay);

    this.unbindCanvas = this.session.bindCanvas(this.previewCanvas);
    this.unbindSession = this.session.onChange(() => {
      this.refreshFromSession();
      this.opts.onChange?.();
    });
  }

  get isExpanded(): boolean {
    return this.expanded;
  }

  getZoom(): number {
    return this.zoom;
  }

  hasSource(): boolean {
    return this.previewCanvas.width > 0 && this.previewCanvas.height > 0;
  }

  setSource(source: HTMLCanvasElement): void {
    this.session.setSource(source);
    this.previewWrap.hidden = false;
    this.expandBtn.hidden = false;
    this.retakeBtn.hidden = false;
    this.removeBtn.hidden = false;
    this.refreshFromSession();
    const shouldAutoExpand =
      this.opts.autoExpand !== undefined
        ? this.opts.autoExpand
        : this.variant !== 'embedded';
    if (shouldAutoExpand) {
      this.openExpanded();
    }
    this.setStatus(
      shouldAutoExpand
        ? 'Screenshot ready.'
        : 'Screenshot ready. Annotate here, or expand for zoom and precision.'
    );
  }

  clear(): void {
    if (this.expanded) this.closeExpanded();
    this.session.clearMemory();
    this.previewWrap.hidden = true;
    this.expandBtn.hidden = true;
    this.retakeBtn.hidden = true;
    this.removeBtn.hidden = true;
    this.previewCanvas.width = 0;
    this.previewCanvas.height = 0;
    this.opts.onChange?.();
  }

  openExpanded(): void {
    if (!this.hasSource()) return;
    this.expanded = true;
    this.overlay.hidden = false;
    this.viewport.appendChild(this.previewCanvas);
    this.setZoomFit();
    this.unbindPan?.();
    this.unbindPan = this.bindPan();
    this.unbindKeys?.();
    this.unbindKeys = this.bindKeys();
    this.opts.onExpandedChange?.(true);
    const focusTarget = this.overlay.querySelector('button') as HTMLElement | null;
    focusTarget?.focus();
  }

  closeExpanded(): void {
    if (!this.expanded) return;
    this.expanded = false;
    this.overlay.hidden = true;
    this.unbindPan?.();
    this.unbindPan = null;
    this.unbindKeys?.();
    this.unbindKeys = null;
    this.previewCanvas.style.transform = '';
    this.previewWrap.appendChild(this.previewCanvas);
    this.opts.onExpandedChange?.(false);
    this.expandBtn.focus();
  }

  destroy(): void {
    this.closeExpanded();
    this.unbindCanvas?.();
    this.unbindSession?.();
    this.unbindPan?.();
    this.unbindKeys?.();
    this.overlay.remove();
    this.root.remove();
  }

  private refreshFromSession(): void {
    const flat = this.session.flatten();
    if (flat) this.session.paintTo(this.previewCanvas);
    this.syncToolPressed();
  }

  private setActiveTool(tool: AnnotationTool): void {
    this.session.setTool(tool);
    this.syncToolPressed();
  }

  private syncToolPressed(): void {
    const tool = this.session.getTool();
    const apply = (buttons: HTMLButtonElement[]) => {
      buttons.forEach((btn) => {
        const active = btn.getAttribute('data-nv-tool') === tool;
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };
    apply(this.compactToolButtons);
    apply(this.editorToolButtons);
  }

  private buildToolBar(forEditor: boolean): HTMLElement {
    const tools = document.createElement('div');
    tools.className = forEditor
      ? 'nv-snap-screen-chrome nv-snap-editor-toolbar'
      : 'nv-snap-screen-chrome';
    tools.setAttribute('role', 'toolbar');
    tools.setAttribute(
      'aria-label',
      forEditor ? 'Evidence annotation tools' : 'Annotation tools'
    );

    const annotate = document.createElement('div');
    annotate.className = 'nv-snap-tool-group';
    annotate.setAttribute('role', 'group');
    annotate.setAttribute('aria-label', 'Annotate');
    for (const def of ANNOTATE_TOOLS) {
      annotate.appendChild(this.toolButton(def));
    }

    const history = document.createElement('div');
    history.className = 'nv-snap-tool-group';
    history.setAttribute('role', 'group');
    history.setAttribute('aria-label', 'History');
    history.append(
      this.btn('Undo', () => this.session.undo()),
      this.btn('Redo', () => this.session.redo()),
      this.btn('Delete', () => {
        if (!this.session.deleteSelected()) {
          this.setStatus('Select an annotation first, or use Undo.');
        }
      }),
      this.btn('Clear', () => this.session.clear())
    );

    tools.append(annotate, history);
    return tools;
  }

  private toolButton(def: ToolDef): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nv-snap-btn';
    btn.setAttribute('data-nv-tool', def.tool || '');
    btn.setAttribute('aria-pressed', 'false');
    btn.setAttribute('aria-label', def.label);
    safeText(btn, def.label);
    btn.addEventListener('click', () => {
      const next = this.session.getTool() === def.tool ? null : def.tool;
      this.setActiveTool(next);
    });
    return btn;
  }

  private setZoomFit(): void {
    if (!this.hasSource()) return;
    const stageW = this.stage.clientWidth || window.innerWidth;
    const stageH = this.stage.clientHeight || window.innerHeight * 0.7;
    const fit = Math.min(stageW / this.previewCanvas.width, stageH / this.previewCanvas.height, 1);
    this.zoom = Math.max(0.15, fit);
    this.panX = 0;
    this.panY = 0;
    this.applyTransform();
  }

  private setZoomActual(): void {
    this.zoom = 1;
    this.applyTransform();
  }

  private adjustZoom(delta: number): void {
    this.zoom = Math.min(4, Math.max(0.15, this.zoom + delta));
    this.applyTransform();
  }

  private applyTransform(): void {
    this.previewCanvas.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
  }

  private bindPan(): () => void {
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const onDown = (evt: PointerEvent) => {
      if (evt.button !== 0) return;
      const tool = this.session.getTool();
      if (!evt.altKey && tool && tool !== 'select') return;
      dragging = true;
      lastX = evt.clientX;
      lastY = evt.clientY;
      this.stage.dataset.panning = '1';
      this.stage.setPointerCapture(evt.pointerId);
    };
    const onMove = (evt: PointerEvent) => {
      if (!dragging) return;
      this.panX += evt.clientX - lastX;
      this.panY += evt.clientY - lastY;
      lastX = evt.clientX;
      lastY = evt.clientY;
      this.applyTransform();
    };
    const onUp = (evt: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      delete this.stage.dataset.panning;
      try {
        this.stage.releasePointerCapture(evt.pointerId);
      } catch {
        /* ignore */
      }
    };
    const onWheel = (evt: WheelEvent) => {
      evt.preventDefault();
      this.adjustZoom(evt.deltaY < 0 ? 0.1 : -0.1);
    };
    this.stage.addEventListener('pointerdown', onDown);
    this.stage.addEventListener('pointermove', onMove);
    this.stage.addEventListener('pointerup', onUp);
    this.stage.addEventListener('pointercancel', onUp);
    this.stage.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      this.stage.removeEventListener('pointerdown', onDown);
      this.stage.removeEventListener('pointermove', onMove);
      this.stage.removeEventListener('pointerup', onUp);
      this.stage.removeEventListener('pointercancel', onUp);
      this.stage.removeEventListener('wheel', onWheel);
    };
  }

  private bindKeys(): () => void {
    const onKey = (evt: KeyboardEvent) => {
      if (!this.expanded) return;
      if (evt.key === 'Escape') {
        evt.preventDefault();
        evt.stopPropagation();
        this.closeExpanded();
        return;
      }
      const mod = evt.ctrlKey || evt.metaKey;
      if (mod && evt.key.toLowerCase() === 'z' && !evt.shiftKey) {
        evt.preventDefault();
        this.session.undo();
        return;
      }
      if (mod && (evt.key.toLowerCase() === 'y' || (evt.key.toLowerCase() === 'z' && evt.shiftKey))) {
        evt.preventDefault();
        this.session.redo();
        return;
      }
      if (evt.key === 'Delete' || evt.key === 'Backspace') {
        if (this.session.deleteSelected()) {
          evt.preventDefault();
        }
      }
      if (evt.key === '=' || evt.key === '+') {
        evt.preventDefault();
        this.adjustZoom(0.1);
      }
      if (evt.key === '-') {
        evt.preventDefault();
        this.adjustZoom(-0.1);
      }
      if (evt.key.toLowerCase() === '0') {
        evt.preventDefault();
        this.setZoomFit();
      }
      if (evt.key.toLowerCase() === '1') {
        evt.preventDefault();
        this.setZoomActual();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }

  private btn(label: string, onClick: () => void, primary = false): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = primary ? 'nv-snap-btn nv-snap-btn-primary' : 'nv-snap-btn';
    safeText(btn, label);
    btn.addEventListener('click', onClick);
    return btn;
  }

  private setStatus(message: string): void {
    if (!this.opts.statusTarget) return;
    safeText(this.opts.statusTarget, message);
    this.opts.statusTarget.classList.toggle('d-none', !message);
    this.opts.statusTarget.classList.toggle('is-error', false);
  }
}
